import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { getDocs, query, where, updateDoc } from "firebase/firestore";
import { auth, dbUsers } from "../firebase";
import { ThemeMap, ThemeContextValue } from "../types";

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

const theme: ThemeMap = {
  /* ═══════════════════════════════════════════
     Rose — Main app theme
     ═══════════════════════════════════════════ */
  RoseLight: {
    backgroundColor: "#fff8f6",
    panelColor: "#fef0ed",
    selectedColor: "#fce4df",
    border: "1px solid #f0c8c0",
    color: "#3d1f1a",
    altColor: "#8b6560",
    hoverColor: "#fef0ed",
    secondaryColor: "#e85d75",
    heroGradient: "linear-gradient(to bottom, #c0392b 0%, #d35468 40%, #e87a8a 100%)",
    heroGradientLight: "linear-gradient(to bottom, #d35468 0%, #e87a8a 65%, #fff8f6 100%)",
    textUnavailable: "#b09090",
    todayColor: "#c0392b",
    toggleColor: "#f0c8c0",
    createButton: "#fef5f3",
    heroText: "#4a1520",
    palette: ["#58b86a", "#e0a040", "#e06068", "#a870c8", "#4898d0"],
  },
  RoseDark: {
    backgroundColor: "#1a0a0e",
    panelColor: "#2a1218",
    selectedColor: "#3d1a22",
    border: "1px solid #5c2a35",
    color: "#f0c8d0",
    altColor: "#c06878",
    hoverColor: "#3d1a22",
    secondaryColor: "#e85d75",
    heroGradient: "linear-gradient(to bottom, #10050a 0%, #5c1a2a 40%, #b03050 100%)",
    heroGradientLight: "linear-gradient(to bottom, #5c1a2a 0%, #b03050 65%, #1a0a0e 100%)",
    textUnavailable: "#8a5a65",
    todayColor: "#ff7088",
    toggleColor: "#5c2a35",
    createButton: "#2a1218",
    heroText: "#ffffff",
    palette: ["#66bb6a", "#f0a040", "#e86068", "#b070c8", "#4898d0"],
  },

  /* ═══════════════════════════════════════════
     Ruby
     ═══════════════════════════════════════════ */
  RedDark: {
    backgroundColor: "#100000",
    panelColor: "#240000",
    selectedColor: "#410004",
    border: "1px solid #410004",
    color: "#e8a0a4",
    altColor: "#c45a62",
    hoverColor: "#3a1012",
    secondaryColor: "#ff6b6b",
    heroGradient: "linear-gradient(to bottom, #1a0000 0%, #6b0a0e 40%, #b8363d 100%)",
    heroGradientLight: "linear-gradient(to bottom, #6b0a0e 0%, #b8363d 65%, #100000 100%)",
    textUnavailable: "#8b4a4e",
    todayColor: "#ff8a80",
    toggleColor: "#410004",
    createButton: "#2f0000",
    heroText: "#ffffff",
    palette: ["#66bb6a", "#f4a742", "#ef5350", "#ab47bc", "#42a5f5"],
  },
  RedLight: {
    backgroundColor: "#fff5f5",
    panelColor: "#fee8e8",
    selectedColor: "#fdd5d5",
    border: "1px solid #f0b0b0",
    color: "#3d1a1a",
    altColor: "#8b5050",
    hoverColor: "#fee8e8",
    secondaryColor: "#e05050",
    heroGradient: "linear-gradient(to bottom, #c0392b 0%, #d44040 40%, #e86060 100%)",
    heroGradientLight: "linear-gradient(to bottom, #d44040 0%, #e86060 65%, #fff5f5 100%)",
    textUnavailable: "#b08080",
    todayColor: "#c0392b",
    toggleColor: "#f0b0b0",
    createButton: "#fff0f0",
    heroText: "#4a1a1a",
    palette: ["#4caf50", "#e8a040", "#e05050", "#9c6cc0", "#3d9be0"],
  },

  /* ═══════════════════════════════════════════
     Ocean
     ═══════════════════════════════════════════ */
  BlueDark: {
    backgroundColor: "#0c2833",
    panelColor: "#0F303D",
    selectedColor: "#216580",
    border: "1px solid #4d6d6e",
    color: "#99c8ff",
    altColor: "#265e70",
    hoverColor: "#1a4a5a",
    secondaryColor: "#80cbc4",
    heroGradient: "linear-gradient(to bottom, #051520 0%, #0f4a5e 40%, #2a8ba8 100%)",
    heroGradientLight: "linear-gradient(to bottom, #0f4a5e 0%, #2a8ba8 65%, #0c2833 100%)",
    textUnavailable: "#a3b3b4",
    todayColor: "#4fc3f7",
    toggleColor: "#265e70",
    createButton: "#3a6f81",
    heroText: "#ffffff",
    palette: ["#4caf80", "#f0a040", "#e06060", "#b07cd8", "#4fc3f7"],
  },
  BlueLight: {
    backgroundColor: "#f5f9ff",
    panelColor: "#e8f0fc",
    selectedColor: "#d0e0f8",
    border: "1px solid #a8c8e8",
    color: "#1a2e40",
    altColor: "#506878",
    hoverColor: "#e8f0fc",
    secondaryColor: "#3080c0",
    heroGradient: "linear-gradient(to bottom, #1565c0 0%, #2a8ba8 40%, #50b8e0 100%)",
    heroGradientLight: "linear-gradient(to bottom, #2a8ba8 0%, #50b8e0 65%, #f5f9ff 100%)",
    textUnavailable: "#8098b0",
    todayColor: "#1565c0",
    toggleColor: "#a8c8e8",
    createButton: "#f0f5ff",
    heroText: "#0a2040",
    palette: ["#4caf50", "#e8a040", "#e05050", "#9c6cc0", "#3080c0"],
  },

  /* ═══════════════════════════════════════════
     Amethyst
     ═══════════════════════════════════════════ */
  PurpleDark: {
    backgroundColor: "#0f0a1a",
    panelColor: "#1a1128",
    selectedColor: "#2a1d40",
    border: "1px solid #3d2a5c",
    color: "#e8dff5",
    altColor: "#8b5cf6",
    hoverColor: "#2a1d40",
    secondaryColor: "#ce93d8",
    heroGradient: "linear-gradient(to bottom, #0a0612 0%, #2a1550 40%, #6030a0 100%)",
    heroGradientLight: "linear-gradient(to bottom, #2a1550 0%, #6030a0 65%, #0f0a1a 100%)",
    textUnavailable: "#7a6a94",
    todayColor: "#e0b0ff",
    toggleColor: "#3d2a5c",
    createButton: "#1a1128",
    heroText: "#ffffff",
    palette: ["#69d4a0", "#e8a84c", "#e86080", "#ce93d8", "#60a8e8"],
  },
  PurpleLight: {
    backgroundColor: "#faf5ff",
    panelColor: "#f0e8fa",
    selectedColor: "#e4d8f4",
    border: "1px solid #c8b0e0",
    color: "#2a1a3d",
    altColor: "#6a5080",
    hoverColor: "#f0e8fa",
    secondaryColor: "#8b5cf6",
    heroGradient: "linear-gradient(to bottom, #6030a0 0%, #7c50c0 40%, #a070e0 100%)",
    heroGradientLight: "linear-gradient(to bottom, #7c50c0 0%, #a070e0 65%, #faf5ff 100%)",
    textUnavailable: "#9880b0",
    todayColor: "#6030a0",
    toggleColor: "#c8b0e0",
    createButton: "#f5f0ff",
    heroText: "#2a1540",
    palette: ["#4caf50", "#e8a040", "#e05050", "#8b5cf6", "#3d9be0"],
  },

  /* ═══════════════════════════════════════════
     Emerald
     ═══════════════════════════════════════════ */
  GreenDark: {
    backgroundColor: "#002918",
    panelColor: "#003d24",
    selectedColor: "#00320c",
    border: "1px solid #004f4f",
    color: "#a3f7bf",
    altColor: "#72c59a",
    hoverColor: "#004f4f",
    secondaryColor: "#81c784",
    heroGradient: "linear-gradient(to bottom, #001008 0%, #004830 40%, #009060 100%)",
    heroGradientLight: "linear-gradient(to bottom, #004830 0%, #009060 65%, #002918 100%)",
    todayColor: "#69f0ae",
    toggleColor: "#006666",
    createButton: "#002918",
    textUnavailable: "#5a9a78",
    heroText: "#ffffff",
    palette: ["#69f0ae", "#f0b050", "#e85858", "#b070d8", "#50b8e8"],
  },
  GreenLight: {
    backgroundColor: "#f5fff8",
    panelColor: "#e8f8ed",
    selectedColor: "#d0f0da",
    border: "1px solid #a0d8b0",
    color: "#1a3d22",
    altColor: "#508060",
    hoverColor: "#e8f8ed",
    secondaryColor: "#2e7d32",
    heroGradient: "linear-gradient(to bottom, #2e7d32 0%, #43a047 40%, #66bb6a 100%)",
    heroGradientLight: "linear-gradient(to bottom, #43a047 0%, #66bb6a 65%, #f5fff8 100%)",
    textUnavailable: "#80a890",
    todayColor: "#2e7d32",
    toggleColor: "#a0d8b0",
    createButton: "#f0faf2",
    heroText: "#0a3018",
    palette: ["#4caf50", "#e8a040", "#e05050", "#9c6cc0", "#3d9be0"],
  },

  /* ═══════════════════════════════════════════
     Sunset
     ═══════════════════════════════════════════ */
  SunsetDark: {
    backgroundColor: "#1a0e08",
    panelColor: "#261610",
    selectedColor: "#3d2218",
    border: "1px solid #5c3a2a",
    color: "#f0d5c0",
    altColor: "#c08060",
    hoverColor: "#3d2218",
    secondaryColor: "#ff9a56",
    heroGradient: "linear-gradient(to bottom, #1a0800 0%, #8b3a0f 40%, #e06820 100%)",
    heroGradientLight: "linear-gradient(to bottom, #8b3a0f 0%, #e06820 65%, #1a0e08 100%)",
    textUnavailable: "#8a6a55",
    todayColor: "#ffb74d",
    toggleColor: "#5c3a2a",
    createButton: "#261610",
    heroText: "#ffffff",
    palette: ["#70c870", "#ffb74d", "#e86060", "#c070e0", "#50b0e8"],
  },
  SunsetLight: {
    backgroundColor: "#fffaf5",
    panelColor: "#fef0e4",
    selectedColor: "#fde0c8",
    border: "1px solid #e8c0a0",
    color: "#3d2810",
    altColor: "#8b6840",
    hoverColor: "#fef0e4",
    secondaryColor: "#e07020",
    heroGradient: "linear-gradient(to bottom, #e06820 0%, #f08030 40%, #ffa050 100%)",
    heroGradientLight: "linear-gradient(to bottom, #f08030 0%, #ffa050 65%, #fffaf5 100%)",
    textUnavailable: "#b09070",
    todayColor: "#c05800",
    toggleColor: "#e8c0a0",
    createButton: "#fff5ef",
    heroText: "#3d2010",
    palette: ["#4caf50", "#e8a040", "#e05050", "#9c6cc0", "#3d9be0"],
  },

  /* ═══════════════════════════════════════════
     Teal
     ═══════════════════════════════════════════ */
  TealDark: {
    backgroundColor: "#0a1a1a",
    panelColor: "#102828",
    selectedColor: "#1a3838",
    border: "1px solid #2a5050",
    color: "#c0f0e8",
    altColor: "#60a8a0",
    hoverColor: "#1a3838",
    secondaryColor: "#4dd0b8",
    heroGradient: "linear-gradient(to bottom, #051010 0%, #104040 40%, #1a7868 100%)",
    heroGradientLight: "linear-gradient(to bottom, #104040 0%, #1a7868 65%, #0a1a1a 100%)",
    textUnavailable: "#5a8880",
    todayColor: "#64ffda",
    toggleColor: "#2a5050",
    createButton: "#102828",
    heroText: "#ffffff",
    palette: ["#64ffda", "#f0b050", "#e87070", "#b880e0", "#50c8f0"],
  },
  TealLight: {
    backgroundColor: "#f5fffc",
    panelColor: "#e4f8f4",
    selectedColor: "#c8f0e8",
    border: "1px solid #90d8c8",
    color: "#1a3d35",
    altColor: "#508078",
    hoverColor: "#e4f8f4",
    secondaryColor: "#00897b",
    heroGradient: "linear-gradient(to bottom, #00897b 0%, #26a69a 40%, #4dd0b8 100%)",
    heroGradientLight: "linear-gradient(to bottom, #26a69a 0%, #4dd0b8 65%, #f5fffc 100%)",
    textUnavailable: "#80a8a0",
    todayColor: "#00695c",
    toggleColor: "#90d8c8",
    createButton: "#f0faf8",
    heroText: "#0a3028",
    palette: ["#4caf50", "#e8a040", "#e05050", "#9c6cc0", "#3d9be0"],
  },

  /* ═══════════════════════════════════════════
     Feature Theme — Women's Health
     ═══════════════════════════════════════════ */
  WomensHealthLight: {
    backgroundColor: "#fff5f7",
    panelColor: "#fde8ee",
    selectedColor: "#fad0dc",
    border: "1px solid #f0b0c4",
    color: "#3d1428",
    altColor: "#8b5070",
    hoverColor: "#fde8ee",
    secondaryColor: "#d4508c",
    heroGradient: "linear-gradient(to bottom, #c0306a 0%, #d45888 40%, #e880a8 100%)",
    heroGradientLight: "linear-gradient(to bottom, #d45888 0%, #e880a8 65%, #fff5f7 100%)",
    textUnavailable: "#b08898",
    todayColor: "#c0306a",
    toggleColor: "#f0b0c4",
    createButton: "#fef2f5",
    heroText: "#3d1028",
    palette: ["#e880a8", "#f0a860", "#66bb6a", "#b070d0", "#50a8d8"],
  },
  WomensHealthDark: {
    backgroundColor: "#1a0810",
    panelColor: "#2a1020",
    selectedColor: "#3d1830",
    border: "1px solid #5c2848",
    color: "#f0c8d8",
    altColor: "#c06888",
    hoverColor: "#3d1830",
    secondaryColor: "#e868a0",
    heroGradient: "linear-gradient(to bottom, #10040a 0%, #4a1835 40%, #9a3068 100%)",
    heroGradientLight: "linear-gradient(to bottom, #4a1835 0%, #9a3068 65%, #1a0810 100%)",
    textUnavailable: "#8a5870",
    todayColor: "#ff80b0",
    toggleColor: "#5c2848",
    createButton: "#2a1020",
    heroText: "#ffffff",
    palette: ["#f080a8", "#f0a860", "#66bb6a", "#c080e0", "#50b0e8"],
  },

  /* ═══════════════════════════════════════════
     Feature Theme — Notes & Tasks
     ═══════════════════════════════════════════ */
  NotesTasksLight: {
    backgroundColor: "#ffffff",
    panelColor: "#f5f6f8",
    selectedColor: "#e8eaef",
    border: "1px solid #d4d8e0",
    color: "#1c2030",
    altColor: "#6b7088",
    hoverColor: "#f0f1f4",
    secondaryColor: "#5070c0",
    heroGradient: "linear-gradient(to bottom, #3a5098 0%, #5070c0 40%, #7090d8 100%)",
    heroGradientLight: "linear-gradient(to bottom, #5070c0 0%, #7090d8 65%, #ffffff 100%)",
    textUnavailable: "#8890a8",
    todayColor: "#3a5098",
    toggleColor: "#c8ccd8",
    createButton: "#f8f9fb",
    heroText: "#1a2540",
    palette: ["#4caf50", "#e8a040", "#e05050", "#8b70c8", "#5070c0"],
  },
  NotesTasksDark: {
    backgroundColor: "#10121a",
    panelColor: "#181c28",
    selectedColor: "#222838",
    border: "1px solid #303848",
    color: "#d0d4e0",
    altColor: "#6878a0",
    hoverColor: "#222838",
    secondaryColor: "#6888d0",
    heroGradient: "linear-gradient(to bottom, #080a12 0%, #1a2440 40%, #304878 100%)",
    heroGradientLight: "linear-gradient(to bottom, #1a2440 0%, #304878 65%, #10121a 100%)",
    textUnavailable: "#505878",
    todayColor: "#80a0e8",
    toggleColor: "#303848",
    createButton: "#181c28",
    heroText: "#ffffff",
    palette: ["#66bb6a", "#f0a840", "#e86060", "#a080d8", "#6888d0"],
  },

  /* ═══════════════════════════════════════════
     Feature Theme — Running
     ═══════════════════════════════════════════ */
  RunningLight: {
    backgroundColor: "#f4f8ff",
    panelColor: "#e4eefb",
    selectedColor: "#ccddf5",
    border: "1px solid #a0c0e8",
    color: "#142840",
    altColor: "#486888",
    hoverColor: "#e4eefb",
    secondaryColor: "#2878c8",
    heroGradient: "linear-gradient(to bottom, #1460a8 0%, #2878c8 40%, #48a0e8 100%)",
    heroGradientLight: "linear-gradient(to bottom, #2878c8 0%, #48a0e8 65%, #f4f8ff 100%)",
    textUnavailable: "#7898b8",
    todayColor: "#1460a8",
    toggleColor: "#a0c0e8",
    createButton: "#f0f5ff",
    heroText: "#0a2040",
    palette: ["#48a0e8", "#f0a040", "#e06060", "#9c70c8", "#4caf50"],
  },
  RunningDark: {
    backgroundColor: "#081420",
    panelColor: "#0e1e30",
    selectedColor: "#182a42",
    border: "1px solid #283a58",
    color: "#b0d0f0",
    altColor: "#4878a8",
    hoverColor: "#182a42",
    secondaryColor: "#48a0e8",
    heroGradient: "linear-gradient(to bottom, #040a14 0%, #102040 40%, #285088 100%)",
    heroGradientLight: "linear-gradient(to bottom, #102040 0%, #285088 65%, #081420 100%)",
    textUnavailable: "#406080",
    todayColor: "#60b8ff",
    toggleColor: "#283a58",
    createButton: "#0e1e30",
    heroText: "#ffffff",
    palette: ["#60b8ff", "#f0a860", "#e87070", "#b080e0", "#66bb6a"],
  },

  /* ═══════════════════════════════════════════
     Feature Theme — Fitness / Gym
     ═══════════════════════════════════════════ */
  FitnessLight: {
    backgroundColor: "#fbf7ff",
    panelColor: "#f3ebff",
    selectedColor: "#e4d4ff",
    border: "1px solid #c8a8f0",
    color: "#1f0d3a",
    altColor: "#6e3fc4",
    hoverColor: "#f3ebff",
    secondaryColor: "#7c3aed",
    heroGradient: "linear-gradient(to bottom, #4c1d95 0%, #6d28d9 40%, #8b5cf6 100%)",
    heroGradientLight: "linear-gradient(to bottom, #6d28d9 0%, #8b5cf6 65%, #fbf7ff 100%)",
    textUnavailable: "#9888b8",
    todayColor: "#5b21b6",
    toggleColor: "#c8a8f0",
    createButton: "#f7f0ff",
    heroText: "#160a30",
    palette: ["#7c3aed", "#e05050", "#4caf50", "#e8a040", "#3d9be0"],
  },
  FitnessDark: {
    backgroundColor: "#0a0612",
    panelColor: "#150a24",
    selectedColor: "#241038",
    border: "1px solid #3a1d6a",
    color: "#e0d0f5",
    altColor: "#8b5cf6",
    hoverColor: "#241038",
    secondaryColor: "#a78bfa",
    heroGradient: "linear-gradient(to bottom, #050208 0%, #2d0a5c 40%, #6d28d9 100%)",
    heroGradientLight: "linear-gradient(to bottom, #2d0a5c 0%, #6d28d9 65%, #0a0612 100%)",
    textUnavailable: "#7868a0",
    todayColor: "#c4b5fd",
    toggleColor: "#3a1d6a",
    createButton: "#150a24",
    heroText: "#ffffff",
    palette: ["#a78bfa", "#e86060", "#66bb6a", "#f0a860", "#50b0e8"],
  },

  /* ═══════════════════════════════════════════
     Classic — Pure black & white
     ═══════════════════════════════════════════ */
  ClassicLight: {
    backgroundColor: "#f5f7fc",
    panelColor: "#ffffff",
    selectedColor: "#f0f0f0",
    border: "1px solid #b0bec5",
    color: "#000000",
    altColor: "#444444",
    hoverColor: "#f5f5f5",
    secondaryColor: "#000000",
    heroGradient: "linear-gradient(to bottom, #4a6fa5 0%, #7a9ec7 40%, #a8c4df 100%)",
    heroGradientLight: "linear-gradient(to bottom, #7a9ec7 0%, #a8c4df 65%, #f5f7fc 100%)",
    textUnavailable: "#888888",
    todayColor: "#000000",
    toggleColor: "#cccccc",
    createButton: "#ffffff",
    heroText: "#1a2838",
    palette: ["#4caf50", "#e8a040", "#e05050", "#9c6cc0", "#3d9be0"],
  },
  ClassicDark: {
    backgroundColor: "#000000",
    panelColor: "#1a1a1a",
    selectedColor: "#2a2a2a",
    border: "1px solid #2a2a2a",
    color: "#e8e8e8",
    altColor: "#8a8a8a",
    hoverColor: "#2a2a2a",
    secondaryColor: "#ffffff",
    heroGradient: "linear-gradient(to bottom, #000000 0%, #111111 40%, #1a1a1a 100%)",
    heroGradientLight: "linear-gradient(to bottom, #111111 0%, #1a1a1a 65%, #000000 100%)",
    textUnavailable: "#555555",
    todayColor: "#ffffff",
    toggleColor: "#333333",
    createButton: "#1a1a1a",
    heroText: "#ffffff",
    palette: ["#66bb6a", "#ffa726", "#ef5350", "#ab47bc", "#42a5f5"],
  },

  /* ═══════════════════════════════════════════
     Minimal
     ═══════════════════════════════════════════ */
  BasicLight: {
    backgroundColor: "#f9fafb",
    panelColor: "#ffffff",
    selectedColor: "#f0f0f0",
    border: "1px solid #e0e0e0",
    color: "#111111",
    altColor: "#555555",
    hoverColor: "#f5f5f5",
    secondaryColor: "#e74c6f",
    heroGradient: "linear-gradient(to bottom, #ad1457 0%, #c2185b 40%, #d84b6a 100%)",
    heroGradientLight: "linear-gradient(to bottom, #c2185b 0%, #d84b6a 65%, #f9fafb 100%)",
    textUnavailable: "#888888",
    todayColor: "#d63384",
    toggleColor: "#d1d5db",
    createButton: "#ffffff",
    heroText: "#3a1428",
    palette: ["#4caf50", "#e8a040", "#e05050", "#9c6cc0", "#3d9be0"],
  },
  BasicDark: {
    backgroundColor: "#121212",
    panelColor: "#1e1e1e",
    selectedColor: "#2a2a2a",
    border: "1px solid #2e2e2e",
    color: "#e0e0e0",
    altColor: "#7a7a7a",
    hoverColor: "#2a2a2a",
    secondaryColor: "#b0b0b0",
    heroGradient: "linear-gradient(to bottom, #080c14 0%, #0f1a2e 40%, #1a2d4a 100%)",
    heroGradientLight: "linear-gradient(to bottom, #0f1a2e 0%, #1a2d4a 65%, #121212 100%)",
    textUnavailable: "#555555",
    todayColor: "#64b5f6",
    toggleColor: "#333333",
    createButton: "#1e1e1e",
    heroText: "#ffffff",
    palette: ["#66bb6a", "#ffa726", "#ef5350", "#ab47bc", "#42a5f5"],
  },
};

/** Mirror the user's chosen theme onto their Firestore user doc. Best-effort:
 *  signed-out users and Firestore failures are swallowed silently — the local
 *  theme already persisted via localStorage, so the UX is unaffected. The
 *  Firestore copy exists purely for cross-device sync potential and admin
 *  analytics (theme-popularity stats). */
async function persistThemeToUserDoc(themeKey: string): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  try {
    const snap = await getDocs(query(dbUsers, where("userId", "==", uid)));
    if (snap.empty) return;
    await updateDoc(snap.docs[0].ref, { "settings.theme": themeKey });
  } catch {
    // ignore — theme is non-critical telemetry
  }
}

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const availableThemes = Object.keys(theme);
  const storedTheme = localStorage.getItem("theme");

  const [activeTheme, setActiveTheme] = useState<string>(
    storedTheme && availableThemes.includes(storedTheme) ? storedTheme : "RoseLight"
  );

  useEffect(() => {
    const currentTheme = theme[activeTheme];
    document.body.style.backgroundColor = currentTheme.backgroundColor;
    document.body.style.color = currentTheme.color;
    localStorage.setItem("theme", activeTheme);
  }, [activeTheme]);

  const changeTheme = (newTheme: string) => {
    setActiveTheme(newTheme);
    document.body.className = newTheme;
    localStorage.setItem("theme", newTheme);
    persistThemeToUserDoc(newTheme);
  };

  const contextValue = useMemo(
    () => ({
      theme,
      changeTheme,
      activeTheme,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
